import React from 'react';
import clsx from 'clsx';
import SVGLocationPinIcon from '@/components/_new/svgs/SVGLocationPinIcon';
import CustomImage from '@/components/CustomImage/CustomImage';
import { DEFAULT_POST_IMAGE } from '@/constants/common';
import { RecommenderAvatarImage } from '@/components/shared/RecommenderAvatarImage/RecommenderAvatarImage';
import type Post from '@/models/post';

const STRIP_SCROLLBAR =
  '[scrollbar-width:none] [-ms-overflow-style:none] [-webkit-overflow-scrolling:touch] [&::-webkit-scrollbar]:hidden';
/** Two cards per row (16px gaps desktop, 12px mobile), scrolling sideways past that. */
const CARD_WIDTH = 'w-[calc((100%-16px)/2)] min-w-[160px] shrink-0 max-md:w-[calc((100%-12px)/2)] max-md:min-w-0';
const CARD_PHOTO = 'h-[260px] max-md:h-[120px]';

export interface FeaturedTripCardData {
  id: string;
  title: string;
  destination: string;
  heroImages: string[];
  authorName: string;
  authorAvatar: string;
  postType: 'List' | 'Itinerary';
  /** Mobile "9 places • 3 days" line; parts without a value are left out. */
  placeCount?: number;
  dayCount?: number;
  onClick: (id: string) => void;
}

function formatTripMeta({ placeCount, dayCount }: FeaturedTripCardData): string {
  const parts: string[] = [];
  if (placeCount) parts.push(`${placeCount} ${placeCount === 1 ? 'place' : 'places'}`);
  if (dayCount) parts.push(`${dayCount} ${dayCount === 1 ? 'day' : 'days'}`);
  return parts.join(' • ');
}

/**
 * Figma "Product_Card": photo with frosted author strip, title, location (+ places/days on mobile).
 * Desktop sizes match ProfileTripCard (36px avatar, 56px author bar, 16px title, 13px location).
 */
const FeaturedTripCard: React.FC<{ card: FeaturedTripCardData }> = ({ card }) => {
  const meta = formatTripMeta(card);
  return (
  <button
    type="button"
    onClick={() => card.onClick(card.id)}
    className={clsx(
      CARD_WIDTH,
      'flex snap-start flex-col overflow-hidden rounded-2xl border border-solid border-[#E4E4E7] bg-white p-0 text-left shadow-[0px_6px_16px_rgba(0,0,0,0.12)] transition-transform hover:-translate-y-0.5',
      'max-md:shadow-[0px_0px_20px_rgba(0,0,0,0.15)]',
    )}
  >
    <span className={clsx('relative block w-full bg-[#F2F2F2]', CARD_PHOTO)}>
      {/* Same chain as TripCard: transformImageUrl on raw keys, next photos on error, then the default */}
      <CustomImage
        src={card.heroImages[0] ?? DEFAULT_POST_IMAGE}
        fallbackSources={card.heroImages.slice(1)}
        fallbackSrc={DEFAULT_POST_IMAGE}
        alt={card.title}
        fill
        sizes="(max-width: 768px) 50vw, 400px"
        style={{ objectFit: 'cover' }}
        className="absolute inset-0 h-full w-full"
      />
      <span className="absolute inset-x-0 top-0 flex h-14 items-center gap-2 rounded-t-[15px] bg-[rgba(193,193,193,0.29)] px-4 py-2 shadow-[0px_0px_20px_rgba(45,38,36,0.15)] backdrop-blur-[10px] max-md:h-auto max-md:gap-[6.65px] max-md:px-[13px] max-md:py-[3px] max-md:shadow-[0px_0px_16.6px_rgba(45,38,36,0.15)] max-md:backdrop-blur-[8.3px]">
        <RecommenderAvatarImage url={card.authorAvatar} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover max-md:h-[26px] max-md:w-[26px]" />
        <span className="flex min-w-0 flex-col justify-center gap-1 font-manrope tracking-[0.02em] max-md:gap-px">
          <span className="text-[10px] font-semibold leading-4 text-white max-md:font-normal max-md:leading-[14px]">
            {card.postType === 'List' ? 'List by' : 'Trip by'}
          </span>
          <span className="truncate text-sm leading-[18px] text-white max-md:text-[11.6px] max-md:leading-[17px]">{card.authorName}</span>
        </span>
      </span>
    </span>
    <span className="flex flex-col gap-1.5 px-4 pb-3 pt-[15px] font-manrope tracking-[0.02em] max-md:gap-0.5 max-md:p-3 max-md:tracking-normal">
      <span className="truncate text-base font-semibold text-black max-md:line-clamp-1 max-md:whitespace-normal max-md:text-sm max-md:font-bold max-md:leading-[19px] max-md:text-[#1A1A1C]">
        {card.title}
      </span>
      {card.destination && (
        <span className="flex min-w-0 items-center gap-1 text-[13px] font-medium text-[#76767F] max-md:gap-0.5 max-md:text-[11px] max-md:leading-[15px]">
          <span className="flex shrink-0" aria-hidden>
            <SVGLocationPinIcon color="#76767F" className="h-3.5 w-3.5 max-md:h-[11px] max-md:w-[11px]" />
          </span>
          <span className="truncate">{card.destination}</span>
        </span>
      )}
      {meta && (
        <span className="truncate text-[11px] font-medium leading-[15px] text-[#76767F] md:hidden">{meta}</span>
      )}
    </span>
  </button>
  );
};

interface PlaceModalFeaturedTripsProps {
  totalPostsCount: number;
  associatedPosts: Post[];
  postsLoading: boolean;
  postsError: string | null;
  /** Logged out and the posts endpoint wants a token: show the sign-in prompt. */
  needsSignIn: boolean;
  isCreatingTrip: boolean;
  isEnriching: boolean;
  onCreateTrip: () => void;
  onSignInClick: () => void;
  onRetryPosts: () => void;
  mapPostToCard: (post: Post) => FeaturedTripCardData;
}

const EMPTY_STATE = 'flex flex-col items-start gap-3 py-2';
const EMPTY_TEXT = 'm-0 font-manrope text-sm leading-5 tracking-[0.02em] text-[#525257]';
const DARK_BUTTON =
  'rounded-full bg-[#252528] px-4 py-2 font-manrope text-sm font-medium text-white transition-colors hover:bg-black disabled:cursor-not-allowed disabled:opacity-60';

/** Figma "Featured in N trips & lists": trips and lists that include this place. */
const PlaceModalFeaturedTrips: React.FC<PlaceModalFeaturedTripsProps> = ({
  totalPostsCount,
  associatedPosts,
  postsLoading,
  postsError,
  needsSignIn,
  isCreatingTrip,
  isEnriching,
  onCreateTrip,
  onSignInClick,
  onRetryPosts,
  mapPostToCard,
}) => (
  <section className="flex flex-col gap-3 px-2.5 pb-3.5 max-md:gap-4 max-md:px-0 max-md:pb-0">
    <h3 className="m-0 font-lora text-lg font-semibold leading-6 tracking-[0.5px] text-[#252528]">
      {needsSignIn ? 'Featured in trips & lists' : `Featured in ${postsLoading ? '…' : totalPostsCount} trips & lists`}
    </h3>

    {needsSignIn && (
      <div className={EMPTY_STATE}>
        <p className={EMPTY_TEXT}>Sign in to see which trips &amp; lists include this place.</p>
        <button type="button" onClick={onSignInClick} className={DARK_BUTTON}>
          Sign in
        </button>
      </div>
    )}

    {postsLoading && (
      <div className={clsx('flex gap-4 overflow-hidden max-md:gap-3', STRIP_SCROLLBAR)}>
        {[0, 1].map((i) => (
          <div key={i} className={clsx(CARD_WIDTH, 'overflow-hidden rounded-lg border-[0.5px] border-solid border-[#E4E4E7]')}>
            <div className={clsx('w-full animate-pulse bg-gray-200', CARD_PHOTO)} />
            <div className="space-y-1.5 p-2.5">
              <div className="h-2.5 w-3/4 animate-pulse rounded bg-gray-200" />
              <div className="h-2 w-1/2 animate-pulse rounded bg-gray-200" />
            </div>
          </div>
        ))}
      </div>
    )}

    {postsError && !postsLoading && (
      <div className={EMPTY_STATE}>
        <p className={EMPTY_TEXT}>{postsError}</p>
        <button
          type="button"
          onClick={onRetryPosts}
          className="border-none bg-transparent p-0 font-manrope text-sm font-semibold text-[#FF5212] hover:underline"
        >
          Retry
        </button>
      </div>
    )}

    {!needsSignIn && !postsLoading && !postsError && associatedPosts.length === 0 && (
      <div className={EMPTY_STATE}>
        <p className={EMPTY_TEXT}>Not featured in any trips or lists yet.</p>
        <button type="button" onClick={onCreateTrip} disabled={isCreatingTrip || isEnriching} className={DARK_BUTTON}>
          {isCreatingTrip ? 'Creating…' : isEnriching ? 'Loading…' : 'Create a Trip'}
        </button>
      </div>
    )}

    {!postsLoading && !postsError && associatedPosts.length > 0 && (
      // Mobile: bleeds to the sheet edges with room around the cards so their 20px shadow isn't clipped
      <div
        className={clsx(
          '-mx-1 flex snap-x snap-mandatory gap-4 overflow-x-auto px-1 pb-2',
          'max-md:-mx-5 max-md:-my-4 max-md:scroll-px-5 max-md:gap-3 max-md:px-5 max-md:py-4',
          STRIP_SCROLLBAR,
        )}
      >
        {associatedPosts.map((post) => (
          <FeaturedTripCard key={post._id} card={mapPostToCard(post)} />
        ))}
      </div>
    )}
  </section>
);

export default PlaceModalFeaturedTrips;
